import * as React from "react";
import { useState } from "react";
import cloneDeep from "lodash/cloneDeep";
import defaultTo from "lodash/defaultTo";
import max from "lodash/max";
import get from "lodash/get";
import without from "lodash/without";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { getValueFromInput } from "util/FormsUtils";
import generateId from "logic/generateId";
import { Alert, Button, Col, ControlLabel, FormGroup, HelpBlock, Row, Input } from "components/bootstrap";
import { extractDurationAndUnit } from "components/common/TimeUnitInput";
import { TimeUnitInput, Spinner, TimezoneSelect } from "components/common";
import useUserDateTime from "hooks/useUserDateTime";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import { describeExpression } from "util/CronUtils";
import CorrelationRules from "./CorrelationRules";
import CorrelationPreview from "./CorrelationPreview";
import commonStyles from "./commonStyles.css";
export const TIME_UNITS = ["HOURS", "MINUTES", "SECONDS"];
const getNewRule = () => ({ id: generateId(), occurrence: 1, event_creator_equals: "" });
const CorrelationForm = ({ eventDefinition, validation, eventDefinitions, onChange }) => {
  const { t } = useTranslation("enterprise_security");
  const { execute_every_ms: executeEveryMs, search_within_ms: searchWithinMs } = eventDefinition.config;
  const searchWithin = extractDurationAndUnit(searchWithinMs, TIME_UNITS);
  const executeEvery = extractDurationAndUnit(executeEveryMs, TIME_UNITS);
  const [searchWithinMsDuration, setSearchWithinMsDuration] = useState(searchWithin.duration);
  const [searchWithinMsUnit, setSearchWithinMsUnit] = useState(searchWithin.unit);
  const [executeEveryMsDuration, setExecuteEveryMsDuration] = useState(executeEvery.duration);
  const [executeEveryMsUnit, setExecuteEveryMsUnit] = useState(executeEvery.unit);
  const [cronDescription, setCronDescription] = useState(
    eventDefinition.config.cron_expression ? describeExpression(eventDefinition.config.cron_expression) : ""
  );
  const { userTimezone } = useUserDateTime();
  const sendTelemetry = useSendTelemetry("event-definition-condition");
  const updateSources = (config, currentEventDefinitionId, nextEventDefinitionId) => {
    const nextConfig = cloneDeep(config);
    const isEventDefinitionUsed = nextConfig.rules.filter((r) => r.event_creator_equals === currentEventDefinitionId).length > 1;
    if (!isEventDefinitionUsed) {
      nextConfig.sources = nextConfig.sources.filter((s) => s.event_definition_id !== currentEventDefinitionId);
    }
    if (nextEventDefinitionId === void 0) {
      return nextConfig;
    }
    const isNextEventDefinitionInSources = nextConfig.sources.find(
      (s) => s.event_definition_id === nextEventDefinitionId
    );
    if (!isNextEventDefinitionInSources) {
      const nextEventDefinition = eventDefinitions.find((d) => d.id === nextEventDefinitionId);
      if (!nextEventDefinition) {
        throw new Error(`Could not find event definition with id: ${nextEventDefinitionId}`);
      }
      const source = {
        title: nextEventDefinition.title,
        event_definition_type: nextEventDefinition.config.type,
        event_definition_id: nextEventDefinition.id
      };
      nextConfig.sources.push(source);
    }
    return nextConfig;
  };
  const handleTimeRangeChange = (key) => (nextValue, nextUnit) => {
    if (key === "search_within_ms") {
      if (nextUnit !== searchWithinMsUnit) {
        sendTelemetry(TELEMETRY_EVENT_TYPE.EVENTDEFINITION_CONDITION.CORRELATION_WITHIN_THE_LAST_UNIT_CHANGED, {
          app_action_value: "searchWithinMsUnit-select",
          new_unit: nextUnit
        });
      }
      setSearchWithinMsDuration(nextValue);
      setSearchWithinMsUnit(nextUnit);
    } else {
      if (nextUnit !== executeEveryMsUnit) {
        sendTelemetry(TELEMETRY_EVENT_TYPE.EVENTDEFINITION_CONDITION.CORRELATION_EXECUTE_EVERY_UNIT_CHANGED, {
          app_action_value: "executeEveryMsUnit-select",
          new_unit: nextUnit
        });
      }
      setExecuteEveryMsDuration(nextValue);
      setExecuteEveryMsUnit(nextUnit);
    }
    const config = cloneDeep(eventDefinition.config);
    config[key] = moment.duration(max([nextValue, 1]), nextUnit).asMilliseconds();
    onChange("config", config);
  };
  const handleRuleChange = (id, nextRule) => {
    let config = cloneDeep(eventDefinition.config);
    const rule = config.rules.find((r) => r.id === id);
    const ruleIdx = config.rules.indexOf(rule);
    if (rule && rule.event_creator_equals !== nextRule.event_creator_equals) {
      config = updateSources(config, rule.event_creator_equals, nextRule.event_creator_equals);
    }
    config.rules[ruleIdx] = nextRule;
    onChange("config", config);
  };
  const handleConfigChange = (event) => {
    const { name } = event.target;
    const config = cloneDeep(eventDefinition.config);
    config[name] = getValueFromInput(event.target);
    if (name === "_is_scheduled") {
      sendTelemetry(TELEMETRY_EVENT_TYPE.EVENTDEFINITION_CONDITION.CORRELATION_EXECUTED_AUTOMATICALLY_TOGGLED, {
        app_action_value: "enable-checkbox",
        is_scheduled: config[name]
      });
    }
    onChange("config", config);
  };
  const handleTimezoneChange = (timezone) => {
    const config = cloneDeep(eventDefinition.config);
    config.cron_timezone = timezone;
    onChange("config", config);
  };
  const handleUseCronSchedulingChange = (event) => {
    const { name } = event.target;
    const config = cloneDeep(eventDefinition.config);
    const value = getValueFromInput(event.target);
    config[name] = value;
    if (value) {
      config.cron_expression = "";
      config.cron_timezone = userTimezone;
    } else {
      config.cron_expression = null;
      config.cron_timezone = null;
    }
    onChange("config", config);
  };
  const handleAddEvent = () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.EVENTDEFINITION_CONDITION.CORRELATION_ADD_EVENT_CLICKED, {
      app_action_value: "add-event-button"
    });
    const config = cloneDeep(eventDefinition.config);
    config.rules.push(getNewRule());
    onChange("config", config);
  };
  const handleRemoveEvent = (id) => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.EVENTDEFINITION_CONDITION.CORRELATION_REMOVE_EVENT_CLICKED, {
      app_action_value: "remove-event-button"
    });
    let config = cloneDeep(eventDefinition.config);
    const rule = config.rules.find((r) => r.id === id);
    config.rules = without(config.rules, rule);
    if (rule && rule.event_creator_equals) {
      config = updateSources(config, rule.event_creator_equals, void 0);
    }
    onChange("config", config);
  };
  if (!eventDefinition.config || !eventDefinition.config.rules || eventDefinition.config.rules.length === 0) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  const ruleErrors = get(validation, "errors.rules", []);
  const eventCreatorErrors = get(validation, "errors.event_creator_equals", []);
  const errors = [...ruleErrors, ...eventCreatorErrors];
  return /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 7, lg: 6 }, /* @__PURE__ */ React.createElement("h2", { className: commonStyles.title }, t("correlation_form.title")), /* @__PURE__ */ React.createElement("p", null, t("correlation_form.description")), eventDefinitions.length === 0 ? /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info" }, t("correlation_form.no_event_definitions")) : /* @__PURE__ */ React.createElement("div", { className: commonStyles.correlationForm }, /* @__PURE__ */ React.createElement("fieldset", null, /* @__PURE__ */ React.createElement(FormGroup, { validationState: validation.errors.search_within_ms ? "error" : null }, /* @__PURE__ */ React.createElement(ControlLabel, null, t("correlation_form.sequence_label")), /* @__PURE__ */ React.createElement(
    TimeUnitInput,
    {
      update: handleTimeRangeChange("search_within_ms"),
      value: searchWithinMsDuration,
      unit: searchWithinMsUnit,
      units: TIME_UNITS,
      clearable: true,
      required: true
    }
  ), validation.errors.search_within_ms && /* @__PURE__ */ React.createElement(HelpBlock, null, validation.errors.search_within_ms[0])), eventDefinition.config.use_cron_scheduling ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    FormGroup,
    {
      controlId: "cron-expression",
      validationState: validation.errors.cron_expression ? "error" : null
    },
    /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "cron-expression",
        name: "cron_expression",
        label: t("correlation_form.cron_expression_label"),
        type: "text",
        help: /* @__PURE__ */ React.createElement("span", null, cronDescription || t("correlation_form.cron_expression_help_default")),
        value: defaultTo(eventDefinition.config.cron_expression, ""),
        onBlur: () => setCronDescription(describeExpression(eventDefinition.config.cron_expression)),
        onChange: handleConfigChange
      }
    ),
    validation.errors.cron_expression && /* @__PURE__ */ React.createElement(HelpBlock, null, get(validation, "errors.cron_expression[0]"))
  ), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(ControlLabel, null, t("correlation_form.cron_timezone_label")), /* @__PURE__ */ React.createElement(
    TimezoneSelect,
    {
      id: "cron_timezone",
      value: defaultTo(eventDefinition.config.cron_timezone, userTimezone),
      name: "cron_timezone",
      clearable: false,
      onChange: handleTimezoneChange
    }
  ))) : /* @__PURE__ */ React.createElement(FormGroup, { validationState: validation.errors.execute_every_ms ? "error" : null }, /* @__PURE__ */ React.createElement(ControlLabel, null, t("correlation_form.execute_every_label")), /* @__PURE__ */ React.createElement(
    TimeUnitInput,
    {
      update: handleTimeRangeChange("execute_every_ms"),
      value: executeEveryMsDuration,
      unit: executeEveryMsUnit,
      units: TIME_UNITS,
      clearable: true,
      required: true
    }
  ), validation.errors.execute_every_ms && /* @__PURE__ */ React.createElement(HelpBlock, null, validation.errors.execute_every_ms[0])), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "is-cron-checkbox",
      type: "checkbox",
      name: "use_cron_scheduling",
      label: t("correlation_form.use_cron_scheduling_label"),
      help: t("correlation_form.use_cron_scheduling_help"),
      checked: defaultTo(eventDefinition.config.use_cron_scheduling, false),
      onChange: handleUseCronSchedulingChange
    }
  ), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "schedule-checkbox",
      type: "checkbox",
      name: "_is_scheduled",
      label: t("correlation_form.enable_label"),
      help: t("correlation_form.enable_help"),
      checked: defaultTo(eventDefinition.config._is_scheduled, true),
      onChange: handleConfigChange
    }
  )), errors.length > 0 && /* @__PURE__ */ React.createElement(
    Alert,
    {
      bsStyle: "danger",
      className: commonStyles.validationSummary,
      title: t("correlation_form.errors_title")
    },
    /* @__PURE__ */ React.createElement("p", null, t("correlation_form.errors_description")),
    /* @__PURE__ */ React.createElement("ul", null, errors.map((error) => /* @__PURE__ */ React.createElement("li", { key: error }, error)))
  ), /* @__PURE__ */ React.createElement(
    CorrelationRules,
    {
      rules: eventDefinition.config.rules,
      eventDefinitions,
      onChange: handleRuleChange,
      onRemove: handleRemoveEvent
    }
  ), /* @__PURE__ */ React.createElement(Button, { bsStyle: "primary", onClick: handleAddEvent }, t("correlation_form.add_event")))), /* @__PURE__ */ React.createElement(Col, { md: 5, lg: 5, lgOffset: 1 }, eventDefinitions.length > 0 && /* @__PURE__ */ React.createElement(CorrelationPreview, { rules: eventDefinition.config.rules, eventDefinitions })));
};
CorrelationForm.defaultConfig = {
  sources: [],
  rules: [getNewRule()],
  search_within_ms: 5 * 60 * 1e3,
  execute_every_ms: 5 * 60 * 1e3,
  _is_scheduled: true
};
export default CorrelationForm;
